import { PropsWithChildren, useEffect, useMemo, useRef } from 'react'

import { useEventListener, useMemoizedFn } from 'ahooks'
import { Popover } from 'antd'
import { createStyles } from 'antd-style'

import { KeySet } from '@eastcoal/common/types/utilities'
import { toArray } from '@eastcoal/common/utils/array'

import { useEditor } from '../../hooks/useEditor'

import { FloatPopoverProps } from './interface'

const useStyles = createStyles(({ prefixCls }) => ({
  popover: {
    [`& .${prefixCls}-popover-inner`]: {
      padding: 0
    }
  },
  trigger: {
    position: 'fixed',
    zIndex: -1,
    opacity: 0,
    visibility: 'hidden'
  }
}))

const events = [
  'click',
  'contextmenu',
  'mouseenter',
  'mouseleave'
] satisfies KeySet<HTMLElementEventMap>[]

export const FloatPopover = ({
  nodeKey,
  children,

  /* -------------------------------------------------------------------------- */
  /*                                Popover Props                               */
  /* -------------------------------------------------------------------------- */
  open,
  trigger = 'hover',
  placement = 'leftBottom',
  destroyOnHidden = false,
  onOpenChange
}: PropsWithChildren<FloatPopoverProps>) => {
  const triggerRef = useRef<HTMLSpanElement>(null)

  const editor = useEditor()

  useEffect(() => {
    if (!nodeKey) {
      return
    }

    const editorDom = editor.getElementByKey(nodeKey)
    const triggerDom = triggerRef.current

    if (!(editorDom && triggerDom)) {
      return
    }

    if (open) {
      const { x, y, height, width } = editorDom.getBoundingClientRect()
      triggerDom.style.top = `${y}px`
      triggerDom.style.left = `${x}px`
      triggerDom.style.height = `${height}px`
      triggerDom.style.width = `${width}px`
    } else {
      triggerDom.style.top = '-9999px'
      triggerDom.style.left = '-9999px'
      triggerDom.style.height = '0px'
      triggerDom.style.width = '0px'
    }
  }, [editor, nodeKey, open])

  const delayRef = useRef<number>(undefined)
  const triggers = useMemo(() => toArray(trigger as string), [trigger])

  const setOpen = (event: string, open: boolean, delay?: number) => {
    if (triggers.includes(event)) {
      if (!delay) {
        onOpenChange?.(open)
      } else {
        delayRef.current = window.setTimeout(() => {
          onOpenChange?.(open)
        }, delay)
      }
    }
  }

  const clearDelayTimer = () => {
    clearTimeout(delayRef.current)
  }

  const handlePopoverEnter = () => {
    clearDelayTimer()
  }

  const handleNodeEvent = useMemoizedFn((event: MouseEvent) => {
    switch (event.type) {
      case 'click':
        setOpen('click', !open)
        break
      case 'contextmenu':
        setOpen('context', !open)
        break
      case 'mouseenter':
        setOpen('hover', true)
        break
      case 'mouseleave':
        setOpen('hover', false, 100)
        break
    }
  })

  useEffect(() => {
    if (!nodeKey) {
      return
    }

    const editorDom = editor.getElementByKey(nodeKey)

    if (!editorDom) {
      return
    }

    events.forEach(event => {
      editorDom.addEventListener(event, handleNodeEvent)
    })

    return () => {
      events.forEach(event => {
        editorDom.removeEventListener(event, handleNodeEvent)
      })
    }
  }, [editor, nodeKey, handleNodeEvent])

  useEventListener(
    'keydown',
    event => {
      if (open && event.code === 'Escape') {
        onOpenChange?.(false)
      }
    },
    {
      target: document
    }
  )

  const { styles } = useStyles()

  return (
    <Popover
      arrow={false}
      destroyOnHidden={destroyOnHidden}
      open={open}
      placement={placement}
      rootClassName={styles.popover}
      trigger={trigger}
      content={
        <div
          onMouseEnter={handlePopoverEnter}
          onPointerEnter={handlePopoverEnter}
        >
          {children}
        </div>
      }
      onOpenChange={onOpenChange}
    >
      <span ref={triggerRef} className={styles.trigger} />
    </Popover>
  )
}
